import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { I18nObject } from 'types';
import axios from 'axios';
import { useI18nContext, langState } from './I18nProvider';

export type TranslationFunctionType = (path: string,
  object?: I18nObject | null,
  fallback?: string
) => string;

function loadNamespaces(path: string, lang: string, namespaces: string[], callback?: Function) {
  const promises = namespaces.map(
    key => axios.get(`${path}/${lang}/${key}.json`)
  );
  Promise.all(promises).then((data) => {
    callback && callback(data.map(res => {
      const result = res.data;
      return Object.keys(result).length === 1 && result[lang] ? result[lang] : result;
    }));
  });
}

function loadNamespacesWithShared(filepath: string, lang: string, namespaces: string[], callback?: Function) {

  const promises = namespaces.map(ns => {
    if (ns.startsWith('shared.')) {
      const dotIndex = ns.indexOf('.');
      const compName = ns.slice(dotIndex + 1);
      return import(`./locales/${lang}/${compName}.json`);
    } else {
      return axios.get(`${filepath}/${lang}/${ns}.json`);
    }
  });

  Promise.all(promises).then((data) => {
    callback && callback(data.map(res => {
      const result = res.data || res.default;
      return Object.keys(result).length === 1 && result[lang] ? result[lang] : result;
    }));
  });
}

function translate(
  path: string,
  resource: any,
  prevResource: any,
  object?: I18nObject | null,
  fallback: string = '',
  singleNs: string = '',
): string {
  if (!resource) {
    if (prevResource) {
      return translate(path, prevResource, null, object, fallback, singleNs);
    }
    return process.env.NODE_ENV === 'production' ? '' : path.split('.').slice(-1)[0];
  }
  let result;
  let namespace, key;

  if(path.includes(':')) {
    namespace = path.split(':')[0] || 'translation';
    key = path.split(':')[1] || '';
  } else {
    namespace = singleNs || 'translation';
    key = path;
  }

  let template = (resource[namespace] || {})[key];

  if (object && template) {
    // populate default object
    const templateRegex = /{([^}{]+?)}/g;
    const variableNames = [];
    let variableNameMatchResult;
    while (
      ((variableNameMatchResult = templateRegex.exec(template)),
        variableNameMatchResult)
    ) {
      variableNames.push(variableNameMatchResult[1]);
    }
    const defaultObject = {};
    variableNames.forEach(name => {
      _.set(defaultObject, name, '');
    });

    const compiled = _.template(template, {
      interpolate: /{([^}{]+?)}/g,
    });
    result = compiled(_.merge(defaultObject, object));
  } else {
    result = template;
  }

  if (typeof result === 'undefined') {
    let prevResult;
    if (prevResource) {
      prevResult = translate(path, prevResource, null, object, fallback, singleNs);
    }

    if (prevResult) return prevResult;
    if (fallback) return fallback;

    if (process.env.NODE_ENV === 'production') {
      return '';
    }
    return key.split('.').slice(-1)[0];
  }

  return result;
}

function getLoadingState(resources: Record<string, any>, lang: string, namespaces: string[]) {
  const targetResource = resources[lang];
  if (!targetResource) return true;

  const keys = Object.keys(targetResource);
  return namespaces.some(key => !keys.includes(key));
}

export function useTranslation(ns?: string[] | string) {
  const i18n = useI18nContext() || {};

  const { resources, language, resourcePath, updateResources, usedNamespaces, updateNamespaces } = i18n;
  
  const singleNs = (!_.isEmpty(ns) && typeof ns === 'string') ? ns : '';
  let namespaces: string[];
  if(_.isEmpty(ns)) {
    namespaces = ['translation'];
  } else {
    namespaces = typeof ns === 'string' ? [ns] : ns as string[];
  };

  let ready = false;
  let newKeys: string[] = [];
  if (namespaces && Array.isArray(namespaces)) {
    newKeys = namespaces.filter(key => !(usedNamespaces?.current[language] || []).includes(key));
    if (newKeys.length === 0) ready = true;
  }

  const [isLoading, setIsLoading] = useState<boolean>(getLoadingState(resources, language, namespaces));

  useEffect(() => {
    setIsLoading(getLoadingState(resources, language, namespaces));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  useEffect(() => {
    if (!isLoading) return;

    setIsLoading(getLoadingState(resources, language, namespaces));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resources]);

  const getTranslation: TranslationFunctionType = useMemo(() => {
    return (
      path: string,
      object?: I18nObject | null,
      fallback: string = '',
    ): string => {
      const targetResource = resources[language];
      const prevResource = resources[langState.prevLang];
      return translate(path, targetResource, prevResource, object, fallback, singleNs);
    };
  }, [resources, language, singleNs]);

  const joinedNS = namespaces?.join() || '';

  useEffect(() => {
    if (!ready) {
      const keys = newKeys.filter(key => !(usedNamespaces?.current[language] || []).includes(key));
      if (keys.length === 0) return;

      updateNamespaces?.(language, keys);
      const callback = (data: any[]) => {
        updateResources(language, keys, data);
      };
      if (keys.find(key => key.startsWith('shared'))) {
        loadNamespacesWithShared(resourcePath, language, keys, callback);
      } else {
        loadNamespaces(resourcePath, language, keys, callback);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joinedNS, language]);


  return { W2: getTranslation, isLoading };
}